<template>
  <div class="image-generation">
    <!-- sidenav -->
    <sidenav
      :custom-class="'bg-white'"
      :class="['position-fixed', 'fixed-start']"
      :style="{ zIndex: this.$store.state.isSidenavOpen ? 100 : '' }"
      :chatbots="chatbots"
      :isDeleteMode="isDeleteMode"
      @bot-init="handleInitChat"
      @bot-selected="getConversationHistory"
      @bot-deleted="handleConfirmDeleteBot"
      @handle-delete-mode="handleDeleteMode"
    >
    </sidenav>
    <div class="container-fluid py-4">
      <div class="mt-5">
        <transition name="fade" mode="out-in">
          <default-card
            v-if="isDefaultCard"
            key="default"
            :attached-files="attachedFiles"
            :size="selectedSize"
            :options="sizeOptions"
            @remove-file="removeFile"
            @handle-dragover="handleDragOver"
            @handle-drop="handleDrop"
            @handle-selected-size="handleSelectedSize"
            @upload-files="handleFileUpload"
            @input-message="handleSendMessage"
          />
          <chat-card
            v-else
            key="chat"
            :attached-files="attachedFiles"
            :size="selectedSize"
            :options="sizeOptions"
            :messages="messages"
            @remove-file="removeFile"
            @handle-dragover="handleDragOver"
            @handle-drop="handleDrop"
            :parse-markdown="parseMarkdown"
            :handle-image-load="handleImageLoad"
            @handle-download-image="downloadImage"
            @handle-selected-size="handleSelectedSize"
            @upload-files="handleFileUpload"
            @send-message="handleSendMessage"
            @generate-partial-image="generatePartialImage"
          />
        </transition>
      </div>
    </div>
  </div>
  <!-- Error Popup -->
  <ErrorPopup
    :show="showErrorPopup"
    :message="errorMessage"
    @close="closeErrorPopup"
  />
  <ConfirmDeleteBotPopup
    :show="showConfirmDeleteBotPopup"
    @close="closeConfirmDeleteBotPopup"
    @delete="deleteChatbot"
  />
  <!-- Overlay for processing message -->
  <ProcessingOverlay
    :show="isProcessing || isGenerating"
    :loadingText="loadingText"
  />
</template>

<script>
import ProcessingOverlay from '@/components/ProcessingOverlay.vue'
import ErrorPopup from '@/components/ErrorPopup.vue'
import HTTPService from '@/services/HTTPService.js'
import { cilPlus, cilCloudDownload, cilArrowLeft } from '@coreui/icons'
import ChatCard from '@/views/ImageGeneration/components/ChatCard.vue'
import DefaultCard from '@/views/ImageGeneration/components/DefaultCard.vue'
import { marked } from 'marked'
import DOMPurify from 'isomorphic-dompurify'
import Sidenav from '@/views/MainChat/components/Sidenav/Index.vue'
import { v4 as uuidv4 } from 'uuid'
import { NAV_MASTER } from '@/constants/navMaster'
import ConfirmDeleteBotPopup from '@/components/ConfirmDeleteBotPopup.vue'
export default {
  components: {
    ConfirmDeleteBotPopup,
    Sidenav,
    DefaultCard,
    ChatCard,
    ProcessingOverlay,
    ErrorPopup,
  },
  data() {
    return {
      messages: [],
      userInput: '',
      messageIndex: 0,
      conversationId: '',
      // Icons
      cilPlus,
      cilCloudDownload,
      cilArrowLeft,
      loadingText: '画像を生成中です',
      // Page state
      currentPage: 'generator',

      // Chat state
      currentChatId: 1,
      chatHistory: [{ id: 1, title: 'New Image', messages: [] }],
      attachedFiles: [],

      // Generation state
      prompt: '',
      selectedSize: '1024x1024',
      sizeOptions: [
        { value: '1024x1024', label: '1024x1024[1:1]' },
        { value: '1024x1536', label: '1024x1536[2:3]' },
        { value: '1536x1024', label: '1536x1024[3:2]' },
        { value: '1024x1824', label: '1024x1824[9:16]' },
        { value: '1824x1024', label: '1824x1024[16:9]' },
      ],
      isGenerating: false,
      isProcessing: false,

      // Edit state
      editingImage: null,
      editPrompt: '',
      isEditProcessing: false,
      maxPerImageFileMB: 15,
      bot_id: '',
      delete_bot_id: '',
      botName: '新規チャットルーム',
      // Selection state
      isSelecting: false,
      selection: { x: 0, y: 0, width: 0, height: 0 },
      startPoint: { x: 0, y: 0 },
      hasSelection: false,
      canvasScale: 1,
      originalImageSize: { width: 0, height: 0 },
      chatbots: [],
      isDeleteMode: false,
      isDefaultCard: true,

      // Error handling
      errorMessage: '',
      showErrorPopup: false,
      showConfirmDeleteBotPopup: false,
    }
  },
  computed: {
    currentChat() {
      return (
        this.chatHistory.find((chat) => chat.id === this.currentChatId) ||
        this.chatHistory[0]
      )
    },
  },
  methods: {
    handleInitChat() {
      this.bot_id = ''
      this.conversationId = uuidv4()
      this.messages = []
      this.isDefaultCard = true
    },
    // Chat management
    startNewChat() {
      const newId = Math.max(...this.chatHistory.map((chat) => chat.id)) + 1
      const newChat = {
        id: newId,
        title: 'New Image',
        messages: [],
      }
      this.chatHistory.unshift(newChat)
      this.currentChatId = newId
    },

    handleConfirmDeleteBot(botId) {
      this.delete_bot_id = botId
      this.showConfirmDeleteBotPopup = true
    },

    handleDeleteMode(isDeleteMode) {
      this.isDeleteMode = isDeleteMode
    },

    handleSelectedSize(size) {
      this.selectedSize = size
    },

    handleSendMessage(messageText) {
      this.userInput = messageText
      this.sendMessage()
    },

    // Input handling
    handleEnter(event) {
      if (!event.shiftKey && this.prompt.trim()) {
        this.sendMessage()
      }
    },

    adjustTextareaHeight() {
      this.$nextTick(() => {
        const textarea = this.$refs.promptInput
        if (textarea) {
          textarea.style.height = 'auto'
          textarea.style.height = Math.min(textarea.scrollHeight, 120) + 'px'
        }
      })
    },

    removeFile(index) {
      this.attachedFiles.splice(index, 1)
    },

    handleDragOver(event) {
      event.preventDefault()
    },

    handleDrop(event) {
      event.preventDefault()
      const files = Array.from(event.dataTransfer.files)
      this.validateAndAttachFiles(files)
    },

    handleFileUpload(event) {
      const files = Array.from(event.target.files)
      this.validateAndAttachFiles(files)
    },

    validateAndAttachFiles(files) {
      const allowedTypes = ['image/jpeg', 'image/png']

      for (let file of files) {
        // MB換算
        const fileSizeMB = file.size / (1024 * 1024)

        if (fileSizeMB > this.maxPerImageFileMB) {
          this.errorMessage = `・「${file.name}」はサイズが大きすぎます。\r\n・画像は最大 ${this.maxPerImageFileMB}MB までアップロード可能です。\r\n・圧縮して再度お試しください。`
          this.showErrorPopup = true
          return
        }

        if (!allowedTypes.includes(file.type)) {
          this.errorMessage =
            'ファイルの読み込むことができませんでした。\r\n' +
            '以下のファイル形式に対応しています。\r\n' +
            '「png, jpg, jpeg」'
          this.showErrorPopup = true
          return
        }
      }

      this.attachedFiles.push(...files)
      //this.$refs.fileInput.value = ''
    },

    parseMarkdown(text) {
      if (typeof text !== 'string') {
        return '' // Return the text as is if it's not a string
      }

      text = text.replaceAll('\\n', '\n')
      text = text.replaceAll('```markdown', '```')
      try {
        const renderer = new marked.Renderer()
        renderer.link = function (href, title, text) {
          // Renderer() seems to return different parameters depending on the version of marked installed...
          const link = {
            href: href.href ?? href,
            text: href.text ?? text,
          }
          return (
            '<a target="_blank" href="' + link.href + '">' + link.text + '</a>'
          )
        }

        //text = text.replaceAll('```', '')
        let parsed = marked(text, { renderer: renderer })

        // format Bing's source links more nicely
        // 1. replace "[^1^]" with "[1]" (during progress streams)
        parsed = parsed.replace(/\[\^(\d+)\^]/g, '<strong>[$1]</strong>')
        // 2. replace "^1^" with "[1]" (after the progress stream is done)
        parsed = parsed.replace(/\^(\d+)\^/g, '<strong>[$1]</strong>')

        // Allow the iframe to show the images created by Bing Image Creator.
        return DOMPurify.sanitize(parsed, {
          ADD_TAGS: ['iframe'],
          ADD_ATTR: [
            'allow',
            'allowfullscreen',
            'frameborder',
            'scrolling',
            'srcdoc',
            'target',
          ],
        })
      } catch (err) {
        console.error('ERROR', err)
        return null
      }
    },

    // Handle image load error
    handleImageError(image) {
      console.error(`Failed to load image: ${image.url}`)
      this.errorMessage = `Cannot load image: ${image.alt}. Please try again.`
      this.showErrorPopup = true
    },

    // Fixed: Handle image load success and create base64
    handleImageLoad(event, image) {
      const img = event.target

      // Đảm bảo hình ảnh đã load hoàn toàn
      if (img.complete && img.naturalWidth > 0) {
        // Delay để đảm bảo DOM đã render xong
        setTimeout(() => {
          this.createBase64ForImage(img, image)
        }, 100)
      } else {
        // Đợi một chút rồi thử lại
        setTimeout(() => {
          if (img.complete && img.naturalWidth > 0) {
            this.createBase64ForImage(img, image)
          }
        }, 200)
      }
    },

    async startConversation() {
      try {
        // スクロールを末尾にまで移動させる
        this.isHistoryLoaded = true
        const response = await HTTPService.get_conversationByGenerateImag(
          this.conversationId,
        )
        for (const message of response.data) {
          this.messages.push({
            id: message.id,
            text: message.text,
            is_user: message.is_user,
            ...(message.is_user === false && {
              images: [
                {
                  url: message.text,
                },
              ],
            }),
          })
        }

        if (this.$store.state.conversationStatus?.data) {
          const data = this.$store.state.conversationStatus.data

          const matched = data.find(
            (item) =>
              item.bot_id === this.bot_id &&
              item.conversation_id === this.conversationId &&
              item.status === 'processing',
          )

          if (matched) {
            this.messages.push({
              id: 'force-message',
              text: '',
              is_user: false,
              is_preparing: false,
              is_deep_research: true,
            })
          }
        }
      } catch (error) {
        console.log(error)
      }
    },

    async getConversationHistory(chatbotId) {
      try {
        const response = await HTTPService.get_conversation_history(chatbotId)
        if (response.data && response.data.length > 0) {
          this.bot_id = response.data[0].bot_id
          this.conversationId = response.data[0].conversation_id
          this.messages = []
          this.isDefaultCard = false
          // 会話の詳細を取得
          await this.startConversation()
        } else {
          this.bot_id = chatbotId
          this.conversationId = uuidv4()
          this.messages = []
          this.isDefaultCard = false
        }
      } catch (error) {
        console.error(error)
      }
    },

    async getChatbots() {
      try {
        const chatbots = await HTTPService.get_chatbots(
          NAV_MASTER.IMAGE_GENERATION,
        )
        // createdの降順でソート
        chatbots.data.sort((a, b) => new Date(b.created) - new Date(a.created))
        // console.log(chatbots.data)
        this.chatbots = chatbots.data
      } catch (error) {
        console.log(error)
        this.$emit('change-active-component', {
          component: 'Login',
        })
      }
    },

    async createChatbot() {
      this.showModal = false
      this.isGenerating = true

      const formData = new FormData()

      if (this.botName) {
        formData.append('data', JSON.stringify({ bot_name: this.botName }))
      }
      if (this.bot_id) {
        formData.append('bot_id', this.bot_id)
      }

      // 画像生成しチャオ
      formData.append('nav_id', NAV_MASTER.IMAGE_GENERATION)

      await HTTPService.createChatbot(formData)
        .then((response) => {
          this.showConfirmation = true
          this.bot_id = response.data.bot_id
          this.getChatbots()
        })
        .catch((error) => {
          if (error.response && error.response.status === 401) {
            return
          }
          if (error.response && error.response.data.error) {
            this.errorMessage = error.response.data.error

            if (this.errorMessage === 'No objects to concatenate') {
              this.errorMessage =
                'ただいま、選択された言語モデルにアクセス集中しています。<br />回答の生成に通常より時間がかかっています。<br />しばらくお待ちください。'
            }

            this.showErrorPopup = true
            if (
              error.response &&
              (error.response.status === 423 || error.response.status === 424)
            ) {
              this.planerror = true
            }
          } else {
            this.errorMessage =
              'ファイルの読み込むことができませんでした。\r\n' +
              '以下のファイル形式に対応しています。\r\n' +
              '「png, jpg, jpeg」'
            this.showErrorPopup = true
          }
        })
        .finally(() => {
          this.isGenerating = false
        })
    },

    async deleteChatbot() {
      try {
        const data = {
          bot_ids: this.delete_bot_id,
        }
        this.isProcessing = true
        await HTTPService.deleteChatbotByGenerateImage(data)
        await this.getChatbots()
      } catch (error) {
        console.log(error)
      } finally {
        this.handleInitChat()
        this.isDeleteMode = false
        this.isProcessing = false
        this.showConfirmDeleteBotPopup = false
      }
    },

    addMessage(text, is_user, id = '') {
      if (this.messageIndex >= 0 && is_user === false) {
        // if first message then just push
        if (!this.messages[this.messageIndex]) {
          this.messages.push({
            id: id,
            text: '',
            is_user: is_user,
            file: null,
          })
        } else {
          this.messages[this.messageIndex].id = id
          this.messages[this.messageIndex].text += text
          this.messages[this.messageIndex].is_user = is_user
        }
      } else {
        this.messageIndex = this.messages.length

        this.messages.push({
          id,
          text,
          is_user,
          file: null,
        })

        this.messageIndex = this.messages.length - 1
      }
    },

    // Image generation
    async sendMessage() {
      if (!this.userInput && this.attachedFiles.length === 0) return
      this.isDefaultCard = false
      // 自動スクロールを停止
      this.isHistoryLoaded = false
      if (!this.bot_id) {
        // Create chatbot name
        if (this.attachedFiles.length > 0) {
          this.botName = `${this.attachedFiles[0].name}`
        } else {
          // Extract the first sentence or the first 20 characters, whichever is shorter
          const firstSentenceEnd =
            this.userInput.indexOf('.') !== -1
              ? this.userInput.indexOf('.') + 1
              : this.userInput.length
          const firstSentence = this.userInput.slice(0, firstSentenceEnd)

          // Limit to the first 20 characters if needed
          this.botName =
            firstSentence.length > 10
              ? firstSentence.slice(0, 20)
              : firstSentence
        }
      }

      // 1. Create or update bot with current files
      if (!this.bot_id || this.attachedFiles.length > 0) {
        await this.createChatbot()
      }

      // Clear the attached files
      /////this.attachedFiles = []

      if (this.userInput) {
        this.addMessage(this.userInput, true)
        this.messageIndex++
      }

      this.isDefaultCard = false
      this.isGenerating = true
      const originalPrompt = this.userInput
      this.prompt = ''
      this.adjustTextareaHeight()

      try {
        this.scrollToBottom()

        const formData = new FormData()

        formData.append('bot_id', this.bot_id)
        formData.append('conversation_id', this.conversationId)
        formData.append('username', this.$store.state.email)
        formData.append('user_input', originalPrompt)
        formData.append('size', this.selectedSize)
        formData.append('quality', 'medium')
        formData.append('n', 1)

        // 添付画像
        if (this.attachedFiles && this.attachedFiles.length > 0) {
          this.attachedFiles.forEach((file) => {
            formData.append('file', file, file.name)
          })
        }

        const response = await HTTPService.conversationByGenerateImage(formData)

        if (response?.data?.success && response.data.data?.images) {
          const generatedImages = response.data.data.images.map(
            (image, index) => {
              if (!image.url) {
                console.error('Missing URL in image:', image)
                throw new Error('Image URL is missing in response')
              }

              return {
                url: image.url,
                alt: `Generated: ${originalPrompt}`,
                id: Date.now() + '_' + index,
                prompt: originalPrompt,
                revised_prompt: image.revised_prompt || originalPrompt,
                base64: null, // Will be set when image loads
              }
            },
          )

          // 添付画像変数を初期化
          this.attachedFiles = []

          this.messages.push({
            text: 'generate-image',
            is_user: false,
            id: response.data.data.id,
            images: generatedImages,
          })

          //assistantMessage.loading = false
          //assistantMessage.content = `こちらが生成された画像です。`
          //assistantMessage.images = generatedImages
          //this.currentChat.messages = [userMessage, assistantMessage]

          // Pre-create base64 for first image (for editing)
          if (generatedImages.length > 0) {
            await this.preCreateBase64(generatedImages[0])
          }

          window.gtag('event', 'API', {
            event_name: '画像生成しチャオ',
            size: this.selectedSize,
            id: this.$store.state.userId,
          })
        }
      } catch (error) {
        if (error.response && error.response.status === 401) {
          return
        }
        console.error('Error generating image:', error)
        //assistantMessage.loading = false
        //this.currentChat.messages = [userMessage, assistantMessage]

        let errorMsg =
          '画像の作成に失敗しました。お手数ですが、時間を空けてもう一度実行してください。'

        let errorObj = ''
        if (error.response?.data?.error) {
          errorObj = error.response.data.error
        } else if (error.message) {
          errorObj = error.message
        }

        if (errorObj.includes('moderation_blocked')) {
          console.warn('moderation_blocked が含まれています')
          this.errorMessage =
            'ChatGPTより画像生成がブロックされました。<br/>' +
            '以下のような内容が含まれていると判断されるとブロックされます。<br/>' +
            '内容を見直して再度お試しください。<br/><br/>' +
            '・暴力、出血、戦争などの表現<br/>' +
            '・差別、ヘイトスピーチに該当する語句<br/>' +
            '・リアルな人物の写真化（政治家・有名人など）<br/>' +
            '・ヌードや性的な描写'
        } else {
          this.errorMessage = errorMsg
        }

        //this.errorMessage = errorMsg
        //assistantMessage.content = `申し訳ありません。エラーが発生しました： ${errorMsg}`
        //this.currentChat.messages = [userMessage, assistantMessage]
        this.showErrorPopup = true
      } finally {
        this.isGenerating = false
        this.$nextTick(() => {
          this.scrollToBottom()
        })
      }
    },

    // New method: Pre-create base64 for an image
    async preCreateBase64(imageData) {
      try {
        // Wait a bit for the image to be rendered in DOM
        await new Promise((resolve) => setTimeout(resolve, 500))

        const base64 = await this.loadImageAndCreateBase64(imageData.url)
        imageData.base64 = base64
        console.log('Pre-created base64 for image:', imageData.id)
      } catch (error) {
        console.log(
          'Failed to pre-create base64 for image:',
          imageData.id,
          error.message,
        )
        // Don't show error to user, base64 will be created when needed
      }
    },

    // Fixed: Create base64 with proper CORS handling
    createBase64ForImage(imgElement, imageData) {
      try {
        // Method 1: Tạo base64 thông qua fetch với CORS
        this.createBase64ViaFetch(imageData.url, imageData)
          .then((base64) => {
            imageData.base64 = base64
            console.log('Base64 created via fetch for image:', imageData.id)
          })
          .catch((error) => {
            console.log(
              'Fetch method failed, trying canvas method:',
              error.message,
            )
            // Fallback to canvas method
            this.createBase64ViaCanvas(imgElement, imageData)
          })
      } catch (error) {
        console.error('Error creating base64 for image:', imageData.id, error)
      }
    },

    // New method: Create base64 via fetch (bypasses CORS canvas issues)
    async createBase64ViaFetch(imageUrl) {
      try {
        // Fetch image data
        const response = await fetch(imageUrl, {
          mode: 'cors',
          credentials: 'omit',
        })

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`)
        }

        const blob = await response.blob()

        // Convert blob to base64
        return new Promise((resolve, reject) => {
          const reader = new FileReader()
          reader.onload = () => resolve(reader.result)
          reader.onerror = reject
          reader.readAsDataURL(blob)
        })
      } catch (error) {
        throw new Error(`Failed to fetch image: ${error.message}`)
      }
    },

    async generatePartialImage(payload) {
      try {
        this.isGenerating = true

        if (payload.prompt) {
          this.addMessage(payload.prompt, true)
          this.messageIndex++
        }

        const formData = new FormData()

        formData.append('bot_id', this.bot_id)
        formData.append('conversation_id', this.conversationId)
        formData.append('conversation_unique_id', payload.id)
        formData.append('username', this.$store.state.email)
        formData.append('user_input', payload.prompt)
        formData.append('size', this.selectedSize)
        formData.append('n', 1)
        // マスク
        formData.append('mask', payload.mask, `mask-${Date.now()}.png`)

        const response = await HTTPService.conversationByGenerateImage(formData)

        if (response.data.success && response.data.data.images) {
          const images = response.data.data.images.map((img, index) => ({
            url: img.url,
            alt: payload.prompt,
            id: `${Date.now()}-${index}`,
            prompt: payload.prompt,
            revised_prompt: img.revised_prompt || payload.prompt,
          }))

          this.messages.push({
            text: 'generate-image',
            is_user: false,
            id: response.data.data.id,
            images,
          })

          this.$nextTick(() => {
            this.scrollToBottom()
          })
        }
      } catch (e) {
        console.error(e)

        this.errorMessage = '画像編集に失敗しました。'
        this.showErrorPopup = true
      } finally {
        this.isGenerating = false
      }
    },

    // Fallback method: Create base64 via canvas (with proper CORS)
    createBase64ViaCanvas(imgElement, imageData) {
      try {
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')

        // Set canvas size
        canvas.width = imgElement.naturalWidth
        canvas.height = imgElement.naturalHeight

        // Draw image
        ctx.drawImage(imgElement, 0, 0)

        // Try to create base64
        const base64 = canvas.toDataURL('image/png')

        if (base64 && base64.length > 100) {
          imageData.base64 = base64
          console.log('Base64 created via canvas for image:', imageData.id)
        } else {
          console.error(
            'Failed to create valid base64 via canvas for image:',
            imageData.id,
          )
        }
      } catch (error) {
        console.error('Canvas method failed for image:', imageData.id, error)
        // Don't set base64 if it fails
      }
    },

    scrollToBottom() {
      const container = this.$refs.messagesContainer
      if (container) {
        setTimeout(() => {
          container.scrollTop = container.scrollHeight
        }, 100)
      }
    },

    // Fixed: Edit image method with better error handling
    editImage(image) {
      // Show loading state
      this.isProcessing = true

      // Check if base64 exists
      if (image.base64) {
        this.proceedToEdit(image)
        this.isProcessing = false
        return
      }

      // Try to create base64
      this.loadImageAndCreateBase64(image.url)
        .then((base64) => {
          image.base64 = base64
          this.proceedToEdit(image)
        })
        .catch((error) => {
          console.error('Cannot create base64 from image URL:', error)
          this.errorMessage =
            '編集用の画像を準備できませんでした。セキュリティ制限により画像にアクセスできない可能性があります。新しい画像を生成してもう一度お試しください。'
          this.showErrorPopup = true
        })
        .finally(() => {
          this.isProcessing = false
        })
    },
    // Fixed: Load image from URL and create base64
    loadImageAndCreateBase64(imageUrl) {
      return new Promise((resolve, reject) => {
        // Try fetch method first
        this.createBase64ViaFetch(imageUrl)
          .then(resolve)
          .catch((fetchError) => {
            console.log(
              'Fetch method failed, trying image element method:',
              fetchError.message,
            )

            // Fallback to image element method
            const img = new Image()

            // Set CORS before setting src
            //img.crossOrigin = 'anonymous'

            img.onload = () => {
              try {
                const canvas = document.createElement('canvas')
                const ctx = canvas.getContext('2d')

                canvas.width = img.naturalWidth
                canvas.height = img.naturalHeight
                ctx.drawImage(img, 0, 0)

                const base64 = canvas.toDataURL('image/png')

                if (base64 && base64.length > 100) {
                  resolve(base64)
                } else {
                  reject(new Error('Invalid base64 data'))
                }
              } catch (error) {
                console.log(error)
                reject(error)
              }
            }

            img.onerror = () => {
              reject(new Error('Failed to load image'))
            }

            img.src = imageUrl
          })
      })
    },

    backToGenerator() {
      this.currentPage = 'generator'
      this.editingImage = null
      this.editPrompt = ''
      this.clearSelection()
    },

    loadImageToCanvas() {
      if (!this.editingImage || !this.editingImage.base64) {
        this.errorMessage = 'Dữ liệu ảnh không khả dụng để chỉnh sửa'
        this.showErrorPopup = true
        this.backToGenerator()
        return
      }

      const canvas = this.$refs.editCanvas
      const ctx = canvas.getContext('2d')
      const img = new Image()

      img.onload = () => {
        this.originalImageSize = {
          width: img.naturalWidth,
          height: img.naturalHeight,
        }

        // Đặt kích thước canvas theo kích thước thực của ảnh
        canvas.width = img.naturalWidth
        canvas.height = img.naturalHeight
        canvas.style.width = img.naturalWidth + 'px'
        canvas.style.height = img.naturalHeight + 'px'

        this.canvasScale = 1 // Không thu nhỏ

        ctx.clearRect(0, 0, canvas.width, canvas.height)
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
      }

      img.onerror = () => {
        console.error('Không thể tải ảnh cho canvas')
        this.errorMessage = 'Không thể tải ảnh để chỉnh sửa. Vui lòng thử lại.'
        this.showErrorPopup = true
        this.backToGenerator()
      }

      img.src = this.editingImage.base64
    },

    // 5. Phương thức để thực hiện edit sau khi có base64
    proceedToEdit(image) {
      this.editingImage = { ...image }
      this.currentPage = 'editor'
      this.editPrompt = ''
      this.clearSelection()

      this.$nextTick(() => {
        this.loadImageToCanvas()
      })
    },

    // Selection handling
    startSelection(event) {
      const rect = this.$refs.editCanvas.getBoundingClientRect()
      this.startPoint = {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      }
      this.isSelecting = true
      this.hasSelection = false

      const overlay = this.$refs.selectionOverlay
      overlay.style.display = 'block'
      overlay.style.left = this.startPoint.x + 'px'
      overlay.style.top = this.startPoint.y + 'px'
      overlay.style.width = '0px'
      overlay.style.height = '0px'
    },

    updateSelection(event) {
      if (!this.isSelecting) return

      const rect = this.$refs.editCanvas.getBoundingClientRect()
      const currentPoint = {
        x: Math.max(0, Math.min(event.clientX - rect.left, rect.width)),
        y: Math.max(0, Math.min(event.clientY - rect.top, rect.height)),
      }

      const x = Math.min(this.startPoint.x, currentPoint.x)
      const y = Math.min(this.startPoint.y, currentPoint.y)
      const width = Math.abs(currentPoint.x - this.startPoint.x)
      const height = Math.abs(currentPoint.y - this.startPoint.y)

      this.selection = { x, y, width, height }

      const overlay = this.$refs.selectionOverlay
      overlay.style.left = x + 'px'
      overlay.style.top = y + 'px'
      overlay.style.width = width + 'px'
      overlay.style.height = height + 'px'
    },

    endSelection() {
      this.isSelecting = false
      if (this.selection.width > 10 && this.selection.height > 10) {
        this.hasSelection = true
      } else {
        this.clearSelection()
      }
    },

    clearSelection() {
      this.hasSelection = false
      this.selection = { x: 0, y: 0, width: 0, height: 0 }
      if (this.$refs.selectionOverlay) {
        this.$refs.selectionOverlay.style.display = 'none'
      }
    },

    // Apply edit
    async applyEdit() {
      if (!this.editPrompt.trim() || !this.hasSelection || !this.editingImage)
        return

      this.isEditProcessing = true

      try {
        const scaleToOriginal = 1 / this.canvasScale
        const originalSelection = {
          x: Math.round(this.selection.x * scaleToOriginal),
          y: Math.round(this.selection.y * scaleToOriginal),
          width: Math.round(this.selection.width * scaleToOriginal),
          height: Math.round(this.selection.height * scaleToOriginal),
        }

        // Kiểm tra vùng chọn hợp lệ
        if (
          originalSelection.x < 0 ||
          originalSelection.y < 0 ||
          originalSelection.x + originalSelection.width >
            this.originalImageSize.width ||
          originalSelection.y + originalSelection.height >
            this.originalImageSize.height
        ) {
          throw new Error('Vùng chọn nằm ngoài giới hạn ảnh')
        }

        // Tạo mask
        const maskCanvas = document.createElement('canvas')
        maskCanvas.width = this.originalImageSize.width
        maskCanvas.height = this.originalImageSize.height
        const maskCtx = maskCanvas.getContext('2d', { alpha: true })

        // Nền màu trắng (alpha = 255) để giữ nguyên ảnh gốc
        maskCtx.fillStyle = 'rgba(255, 255, 255, 1)'
        maskCtx.fillRect(0, 0, maskCanvas.width, maskCanvas.height)

        // Vùng chọn trong suốt (alpha = 0) để API chỉnh sửa
        maskCtx.clearRect(
          originalSelection.x,
          originalSelection.y,
          originalSelection.width,
          originalSelection.height,
        )

        const maskDataUrl = maskCanvas.toDataURL('image/png')

        // Kiểm tra base64 của ảnh
        if (!this.editingImage.base64) {
          throw new Error('Dữ liệu base64 của ảnh không khả dụng')
        }

        const payload = {
          image: this.editingImage.base64,
          mask: maskDataUrl,
          prompt: this.editPrompt,
          size: this.selectedSize,
          n: 1,
        }

        console.log(
          'Sending edit payload to /edit-image:',
          JSON.stringify(payload, null, 2),
        )
        const editResponse = await HTTPService.edit_image(payload)
        console.log(
          'Edit response from /edit-image:',
          JSON.stringify(editResponse, null, 2),
        )

        if (
          editResponse.data &&
          editResponse.data.success &&
          editResponse.data.data &&
          editResponse.data.data.images
        ) {
          const editedImages = editResponse.data.data.images.map(
            (image, index) => {
              if (!image.url) {
                console.error('Thiếu URL trong ảnh đã chỉnh sửa:', image)
                throw new Error('URL ảnh bị thiếu trong phản hồi chỉnh sửa')
              }
              return {
                url: image.url,
                alt: `Chỉnh sửa: ${this.editPrompt}`,
                id: Date.now() + '_edited_' + index,
                prompt: this.editPrompt,
                revised_prompt:
                  image.revised_prompt || 'Không có prompt sửa đổi',
                base64: null,
              }
            },
          )

          const assistantMessage = {
            id: Date.now(),
            type: 'assistant',
            content: `画像を "${this.editPrompt}" に基づいて編集しました。`,
            images: editedImages,
          }

          this.currentChat.messages.push(assistantMessage)
          this.backToGenerator()

          this.$nextTick(() => {
            this.scrollToBottom()
          })
        } else {
          console.error(
            'Cấu trúc phản hồi chỉnh sửa không hợp lệ:',
            editResponse,
          )
          throw new Error(
            'Không tìm thấy ảnh trong phản hồi chỉnh sửa hoặc cấu trúc không hợp lệ',
          )
        }
      } catch (error) {
        console.error('Lỗi khi chỉnh sửa ảnh:', error)
        this.errorMessage =
          error.response?.data?.error ||
          error.message ||
          '画像を編集できませんでした。後でもう一度お試しください。'
        this.showErrorPopup = true
      } finally {
        this.isEditProcessing = false
      }
    },

    // Utility methods
    downloadImage(url) {
      console.log(url)
      fetch(url, {
        mode: 'cors',
        cache: 'no-store',
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error('画像を読み込めませんでした。')
          }
          return response.blob()
        })
        .then((blob) => {
          const blobUrl = window.URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = blobUrl
          link.download = `generated-image-${Date.now()}.png`
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          setTimeout(() => {
            window.URL.revokeObjectURL(blobUrl)
          }, 1000)
        })
        .catch((error) => {
          console.error('ダウンロード失敗:', error)
          this.errorMessage = '画像の読み込みに失敗しました。再度実行ください。'
          this.showErrorPopup = true
        })
    },

    closeErrorPopup() {
      this.showErrorPopup = false
      this.errorMessage = ''
    },

    closeConfirmDeleteBotPopup() {
      this.showConfirmDeleteBotPopup = false
    },
  },

  async mounted() {
    console.log('loading..' + this.bot_id)
    try {
      console.log('get user..')
      await this.getChatbots()
      if (this.chatbots.length >= 50) {
        await this.getConversationHistory(this.chatbots[0].id)
      }
    } catch (error) {
      console.error(error)
      // If there's an error or no user found, redirect to the login page
      this.$router.push('/login')
    }

    marked.setOptions({
      silent: true,
      xhtml: true,
      breaks: true,
      gfm: true,
    })

    this.messageIndex = 1
    if (this.conversation_id !== '') {
      this.conversationId = this.conversation_id
    }

    if (this.$route.query.conversationId) {
      this.conversationId = this.$route.query.conversationId
    }

    if (!this.conversationId) {
      this.conversationId = uuidv4()
    } else {
      await this.startConversation()
    }

    this.adjustTextareaHeight()
  },
}
</script>

<style scoped>
* {
  box-sizing: border-box;
}

.app-container {
  height: 100vh;
  background: #ffffff;
  font-family:
    -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu,
    Cantarell, sans-serif;
  color: #000000;
  overflow: hidden;
}

/* Generator Page Styles */
.generator-page {
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
}

/* Main Content */
.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.chat-container {
  flex: 1;
  max-width: 768px;
  margin: 0 auto;
  width: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  scroll-behavior: smooth;
  scrollbar-width: thin;
  max-height: calc(100vh - 200px);
}

.chat-messages::-webkit-scrollbar {
  width: 8px;
}

.chat-messages::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.chat-messages::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

.chat-messages::-webkit-scrollbar-thumb:hover {
  background: #555;
}

/* Input Section */
.input-section {
  border-top: 1px solid #e5e7eb;
  background: #ffffff;
  padding: 20px;
  flex-shrink: 0;
  position: sticky;
  bottom: 0;
  z-index: 10;
}

/* Responsive Design */
@media (max-width: 768px) {
  .chat-messages {
    max-height: calc(100vh - 180px);
    padding: 16px;
  }

  .input-section {
    padding: 16px;
  }
}

@media (max-width: 480px) {
  .chat-messages {
    max-height: calc(100vh - 160px);
  }
}

/* Rest of the styles remain unchanged */
.welcome-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 60px 20px;
  color: #6b7280;
}

.welcome-icon {
  width: 48px;
  height: 48px;
  margin-bottom: 24px;
  color: #9ca3af;
}

.welcome-section h2 {
  font-size: 28px;
  font-weight: 600;
  color: #000000;
  margin: 0 0 12px 0;
}

.welcome-section p {
  font-size: 16px;
  color: #6b7280;
  margin: 0;
}

/* Messages */
.message-wrapper {
  margin-bottom: 24px;
}

.message-content {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}

.user-message {
  justify-content: flex-end;
}

.assistant-message {
  justify-content: flex-start;
}

.user-avatar,
.assistant-avatar {
  width: 32px;
  height: 32px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 600;
}

.user-avatar {
  background: #000000;
  color: #ffffff;
}

.assistant-avatar {
  background: #f3f4f6;
  color: #6b7280;
}

.assistant-avatar svg {
  width: 16px;
  height: 16px;
}

.message-text {
  max-width: calc(100% - 44px);
  line-height: 1.5;
}

.user-message .message-text {
  background: #f3f4f6;
  padding: 12px 16px;
  border-radius: 16px;
  border-bottom-right-radius: 4px;
}

.assistant-message .message-text {
  padding: 0;
}

/* Loading Animation */
.loading-container {
  display: flex;
  align-items: center;
  gap: 12px;
  color: #6b7280;
  font-size: 14px;
}

.typing-indicator {
  display: flex;
  gap: 4px;
}

.dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #9ca3af;
  animation: typing 1.4s infinite ease-in-out;
}

.dot:nth-child(1) {
  animation-delay: -0.32s;
}
.dot:nth-child(2) {
  animation-delay: -0.16s;
}

@keyframes typing {
  0%,
  80%,
  100% {
    transform: scale(0.8);
    opacity: 0.5;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}

/* Generated Images */
.generated-images {
  margin-top: 16px;
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
}

.image-container {
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
}

.image-wrapper {
  position: relative;
  width: 100%;
}

.image-wrapper img {
  width: 100%;
  height: auto;
  display: block;
}

.image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s;
}

.image-wrapper:hover .image-overlay {
  opacity: 1;
}

.image-actions {
  display: flex;
  gap: 8px;
}

.action-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 13px;
  font-weight: 500;
  color: #000000;
  cursor: pointer;
  transition: background-color 0.2s;
}

.action-btn:hover {
  background: #ffffff;
}

.action-btn svg {
  width: 14px;
  height: 14px;
}

/* Input Section */
.input-section {
  border-top: 1px solid #e5e7eb;
  background: #ffffff;
  padding: 20px;
  flex-shrink: 0;
}

.input-container {
  max-width: 1000px;
  margin: 0 auto;
  display: flex;
  flex-direction: row; /* Xếp ngang */
  align-items: stretch; /* Căn trên để dropdown không bị lệch */
  gap: 12px; /* Khoảng cách giữa input-wrapper và size-selector */
}

.input-wrapper {
  position: relative;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 24px;
  padding: 12px 60px 12px 20px;
  transition: border-color 0.2s;
  flex: 1; /* input-wrapper chiếm toàn bộ không gian còn lại */
}

.input-wrapper:focus-within {
  border-color: #000000;
  box-shadow: 0 0 0 1px #000000;
}

.prompt-input {
  width: 100%;
  background: transparent;
  border: none;
  outline: none;
  font-size: 16px;
  line-height: 24px;
  resize: none;
  color: #000000;
  min-height: 24px;
  max-height: 96px;
}

.prompt-input::placeholder {
  color: #9ca3af;
}

.input-controls {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  gap: 8px;
}

.size-selector {
  margin-left: 12px;
  display: flex;
  align-items: stretch;
}

.size-dropdown {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 6px 12px;
  font-size: 13px;
  color: #374151;
  cursor: pointer;
  outline: none;
  width: 180px;
}

.size-dropdown:hover {
  border-color: #d1d5db;
}

.send-btn {
  width: 32px;
  height: 32px;
  border-radius: 16px;
  border: none;
  background: #e5e7eb;
  color: #9ca3af;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
}

.send-btn.active {
  background: #000000;
  color: #ffffff;
}

.send-btn:disabled {
  cursor: not-allowed;
}

.send-btn svg {
  width: 16px;
  height: 16px;
}

.spinner-small {
  width: 16px;
  height: 16px;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.spinner-small.white {
  border-top-color: #ffffff;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Edit Page Styles */
.edit-page {
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f9fafb;
}

.edit-header {
  background: #ffffff;
  border-bottom: 1px solid #e5e7eb;
  padding: 16px 24px;
  display: flex;
  align-items: center;
  gap: 16px;
}

.back-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 14px;
  color: #374151;
  cursor: pointer;
  transition: background-color 0.2s;
}

.back-btn:hover {
  background: #e5e7eb;
}

.edit-header h2 {
  font-size: 18px;
  font-weight: 600;
  color: #000000;
  margin: 0;
}

.edit-container {
  flex: 1;
  display: flex;
  overflow: hidden;
}

.edit-main {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  overflow: hidden;
}

.canvas-section {
  max-width: 100%;
  max-height: 100%;
}

.canvas-container {
  position: relative;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  overflow: auto; /* Cho phép cuộn cho ảnh lớn */
  background: #ffffff;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  max-width: 100%;
  max-height: 80vh; /* Tăng để chứa ảnh lớn hơn */
}

.edit-canvas {
  display: block;
  width: auto; /* Sử dụng chiều rộng thực */
  height: auto; /* Sử dụng chiều cao thực */
}

.selection-overlay {
  position: absolute;
  border: 2px solid #3b82f6;
  background: rgba(59, 130, 246, 0.1);
  pointer-events: none;
  display: none;
  border-radius: 2px;
}

.edit-sidebar {
  width: 320px;
  background: #ffffff;
  border-left: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
}

.edit-section {
  padding: 24px;
  flex: 1;
}

.edit-section h3 {
  font-size: 18px;
  font-weight: 600;
  color: #000000;
  margin: 0 0 8px 0;
}

.section-description {
  font-size: 14px;
  color: #6b7280;
  line-height: 1.5;
  margin: 0 0 24px 0;
}

.edit-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-group label {
  font-size: 14px;
  font-weight: 500;
  color: #374151;
}

.edit-textarea {
  width: 100%;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px;
  font-size: 14px;
  color: #000000;
  line-height: 1.5;
  resize: vertical;
  min-height: 80px;
  outline: none;
  transition: border-color 0.2s;
}

.edit-textarea:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 1px #3b82f6;
}

.edit-textarea::placeholder {
  color: #9ca3af;
}

.selection-info {
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 8px;
  padding: 12px;
  font-size: 13px;
}

.info-item {
  display: flex;
  justify-content: space-between;
  margin-bottom: 4px;
}

.info-item:last-child {
  margin-bottom: 0;
}

.info-label {
  color: #374151;
  font-weight: 500;
}

.info-value {
  color: #1f2937;
  font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Roboto Mono', monospace;
}

.edit-actions {
  display: flex;
  gap: 12px;
}

.edit-btn {
  flex: 1;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 44px;
}

.edit-btn.primary {
  background: #000000;
  color: #ffffff;
  border: none;
}

.edit-btn.primary:hover:not(:disabled) {
  background: #333333;
}

.edit-btn.primary:disabled {
  background: #d1d5db;
  color: #9ca3af;
  cursor: not-allowed;
}

.edit-btn.secondary {
  background: #ffffff;
  color: #374151;
  border: 1px solid #e5e7eb;
}

.edit-btn.secondary:hover:not(:disabled) {
  background: #f9fafb;
}

.edit-btn.secondary:disabled {
  background: #f9fafb;
  color: #d1d5db;
  cursor: not-allowed;
}

/* Responsive Design */
@media (max-width: 768px) {
  .chat-messages {
    padding: 16px;
  }

  .input-section {
    padding: 16px;
  }

  .input-container {
    flex-direction: column; /* Chuyển sang xếp dọc trên màn hình nhỏ */
    gap: 8px;
  }

  .input-wrapper {
    padding: 10px 50px 10px 16px;
  }

  .size-selector {
    margin-left: 0; /* Bỏ margin-left trên màn hình nhỏ */
  }

  .size-dropdown {
    width: 100%; /* Dropdown chiếm toàn bộ chiều rộng */
    font-size: 12px;
    padding: 4px 8px;
  }

  .input-wrapper {
    padding: 10px 50px 10px 16px;
  }

  .input-controls {
    flex-direction: column;
    gap: 4px;
    right: 4px;
  }

  .size-dropdown {
    font-size: 12px;
    padding: 4px 8px;
    min-width: 100px;
  }

  .edit-container {
    flex-direction: column;
  }

  .edit-sidebar {
    width: 100%;
    max-height: 40vh;
    overflow-y: auto;
  }

  .edit-main {
    padding: 16px;
  }

  .canvas-container {
    max-width: 100%;
    max-height: 60vh; /* Điều chỉnh cho màn hình nhỏ hơn */
  }

  .edit-canvas {
    width: auto;
    height: auto;
    max-width: 100%; /* Ngăn tràn */
  }
}

@media (max-width: 480px) {
  .header-content {
    padding: 12px 16px;
  }

  .app-title {
    font-size: 14px;
  }

  .new-chat-btn {
    padding: 6px 12px;
    font-size: 13px;
  }

  .welcome-section {
    padding: 40px 16px;
  }

  .welcome-section h2 {
    font-size: 24px;
  }

  .message-content {
    gap: 8px;
  }

  .user-avatar,
  .assistant-avatar {
    width: 28px;
    height: 28px;
    border-radius: 14px;
  }

  .message-text {
    max-width: calc(100% - 36px);
  }

  .generated-images {
    grid-template-columns: 1fr;
  }

  .input-wrapper {
    border-radius: 20px;
    padding: 8px 40px 8px 12px;
  }

  .size-dropdown {
    width: 100%;
  }

  .edit-section {
    padding: 16px;
  }

  .canvas-container {
    max-width: 100%;
    max-height: 50vh;
  }

  .edit-canvas {
    width: auto;
    height: auto;
    max-width: 100%;
  }
}
</style>
